import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../contexts/AuthContext";
import { useAPI } from "../contexts/APIContext";
import { showToast } from "../components/Toast";

const GIFT_PLANS = [
  { id: "1_month", label: "1 Month Access", desc: "The perfect spark of inspiration", price: "$15", months: 1, popular: false },
  { id: "3_months", label: "3 Months Journey", desc: "Deep storytelling exploration", price: "$45", oldPrice: "$55", months: 3, popular: true },
  { id: "6_months", label: "6 Months Odyssey", desc: "Complete narrative mastery", price: "$90", oldPrice: "$110", months: 6, popular: false },
];

export default function GiftPage() {
  const { user } = useAuth();
  const { apiClient } = useAPI();
  const [searchParams, setSearchParams] = useSearchParams();
  const [recipientName, setRecipientName] = useState("");
  const [recipientEmail, setRecipientEmail] = useState("");
  const [senderName, setSenderName] = useState("");
  const [personalMessage, setPersonalMessage] = useState("");
  const [selectedPlanId, setSelectedPlanId] = useState("1_month");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const resetForm = () => {
    setRecipientName("");
    setRecipientEmail("");
    setSenderName("");
    setPersonalMessage("");
    setSelectedPlanId("");
  };

  const selectedPlan = useMemo(
    () => GIFT_PLANS.find((plan) => plan.id === selectedPlanId) || null,
    [selectedPlanId]
  );

  const isContinueDisabled =
    !recipientName.trim() || !recipientEmail.trim() || !selectedPlan || isSubmitting;

  useEffect(() => {
    if (searchParams.get("success") !== "true") return;

    showToast(
      "\ud83c\udf89 Gift sent successfully! The recipient will receive an email shortly.",
      "success"
    );
    resetForm();

    const nextParams = new URLSearchParams(searchParams);
    nextParams.delete("success");
    setSearchParams(nextParams, { replace: true });
  }, [searchParams, setSearchParams]);

  const handleContinue = async (e) => {
    e.preventDefault();

    if (!user?.id || !user?.email) {
      showToast("Please log in to continue.", "error");
      return;
    }

    if (!selectedPlan) {
      showToast("Please select a plan duration.", "error");
      return;
    }

    const payload = {
      type: "gift",
      user_id: user.id,
      user_email: user.email,
      recipient_email: recipientEmail.trim(),
      recipient_name: recipientName.trim(),
      duration_months: selectedPlan.months,
      success_url: `${window.location.origin}/gift?success=true`,
      cancel_url: `${window.location.origin}/gift`,
    };

    setIsSubmitting(true);
    try {
      const response = await apiClient.request("/create-stripe-checkout-session", {
        method: "POST",
        body: JSON.stringify(payload),
      });

      if (response?.status && response?.data?.checkout_url) {
        window.location.href = response.data.checkout_url;
        return;
      }

      showToast(
        response?.error || "Failed to create checkout session. Please try again.",
        "error"
      );
    } catch (error) {
      showToast("Failed to redirect to checkout. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const FREDOKA = { fontFamily: "'Fredoka', sans-serif" };
  const INPUT_BASE = {
    ...FREDOKA,
    width: "100%",
    background: "#FFFFFF",
    color: "#0B1A33",
    fontSize: 15,
    fontWeight: 500,
    border: "2px solid rgba(11,26,51,0.10)",
    borderRadius: 14,
    padding: "12px 16px",
    outline: "none",
    transition: "border-color 200ms, box-shadow 200ms",
  };

  return (
    <div
      className="min-h-screen relative pt-24 pb-16"
      style={{
        background: "linear-gradient(180deg, #F5FAFF 0%, #EAF3FF 100%)",
        fontFamily: "'Inter', 'Fredoka', sans-serif",
      }}
    >
      {/* Soft brand blobs */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div style={{ position: "absolute", top: -200, left: -180, width: 520, height: 520, borderRadius: "50%", background: "rgba(0,101,248,0.08)", filter: "blur(30px)" }} />
        <div style={{ position: "absolute", bottom: -240, right: -220, width: 560, height: 560, borderRadius: "50%", background: "rgba(0,202,255,0.08)", filter: "blur(30px)" }} />
      </div>

      <div className="relative z-10 max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero */}
        <div
          className="mb-8 relative overflow-hidden flex flex-col md:flex-row items-center gap-6 p-8"
          style={{
            background: "linear-gradient(135deg, #0065F8 0%, #00CAFF 100%)",
            borderRadius: 28,
            boxShadow: "0px 6px 0px rgba(7,59,142,0.35), 0px 18px 36px rgba(0,101,248,0.22)",
            color: "#FFFFFF",
          }}
        >
          <div style={{ position: "absolute", top: -80, right: -50, width: 240, height: 240, borderRadius: "50%", background: "rgba(255,255,255,0.14)", filter: "blur(20px)" }} />
          <div className="flex-1 relative z-10">
            <span style={{ ...FREDOKA, background: "rgba(255,255,255,0.20)", color: "#FFFFFF", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase", padding: "5px 12px", borderRadius: 999, border: "1.5px solid rgba(255,255,255,0.30)", display: "inline-block" }}>
              Gift the Magic
            </span>
            <h2 style={{ ...FREDOKA, color: "#FFFFFF", fontWeight: 700, fontSize: "clamp(1.8rem, 4vw, 2.8rem)", textTransform: "uppercase", letterSpacing: "-0.02em", lineHeight: 1.05, margin: "10px 0 8px" }}>
              Share Pixley Premium
            </h2>
            <p style={{ ...FREDOKA, color: "rgba(255,255,255,0.92)", fontSize: 15, margin: 0, maxWidth: 460 }}>
              Send a Pixley subscription to a friend or family member. They'll get their own login and a personal note from you.
            </p>
          </div>
          <div
            className="relative z-10 flex-shrink-0 flex items-center justify-center"
            style={{
              width: 108,
              height: 108,
              borderRadius: 22,
              background: "#FFFFFF",
              border: "2px solid rgba(255,255,255,0.40)",
              boxShadow: "0px 5px 0px rgba(7,59,142,0.35)",
              transform: "rotate(4deg)",
            }}
          >
            <svg width="56" height="56" fill="none" stroke="#0065F8" strokeWidth="2.2" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" d="M20 12v8H4v-8M2 7h20v5H2zM12 22V7M12 7H7.5a2.5 2.5 0 110-5C11 2 12 7 12 7zM12 7h4.5a2.5 2.5 0 100-5C13 2 12 7 12 7z" />
            </svg>
          </div>
        </div>

        <form onSubmit={handleContinue}>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-6">
            {/* Recipient form */}
            <section
              className="lg:col-span-7 p-7"
              style={{
                background: "#FFFFFF",
                border: "2px solid rgba(11,26,51,0.06)",
                borderRadius: 24,
                boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.06)",
              }}
            >
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                Recipient
              </span>
              <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 22px" }}>
                Who's it for?
              </h3>

              <div className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Recipient name</label>
                    <input
                      type="text"
                      value={recipientName}
                      onChange={(e) => setRecipientName(e.target.value)}
                      style={INPUT_BASE}
                      onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                      placeholder="Who is this gift for?"
                      required
                    />
                  </div>
                  <div>
                    <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Recipient email</label>
                    <input
                      type="email"
                      value={recipientEmail}
                      onChange={(e) => setRecipientEmail(e.target.value)}
                      style={INPUT_BASE}
                      onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                      onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                      placeholder="Where should we send it?"
                      required
                    />
                  </div>
                </div>
                <div>
                  <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Your name</label>
                  <input
                    type="text"
                    value={senderName}
                    onChange={(e) => setSenderName(e.target.value)}
                    style={INPUT_BASE}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                    placeholder="Let them know who it's from"
                  />
                </div>
                <div>
                  <label style={{ ...FREDOKA, display: "block", color: "#5A6B85", fontSize: 11, fontWeight: 800, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 8 }}>Personal message</label>
                  <textarea
                    value={personalMessage}
                    onChange={(e) => setPersonalMessage(e.target.value)}
                    style={{ ...INPUT_BASE, resize: "none", padding: "14px 16px" }}
                    onFocus={(e) => { e.currentTarget.style.borderColor = "#0065F8"; e.currentTarget.style.boxShadow = "0 0 0 4px rgba(0,101,248,0.12)"; }}
                    onBlur={(e) => { e.currentTarget.style.borderColor = "rgba(11,26,51,0.10)"; e.currentTarget.style.boxShadow = "none"; }}
                    placeholder="Write a magical message…"
                    rows={4}
                  />
                </div>
              </div>
            </section>

            {/* Plan picker */}
            <section
              className="lg:col-span-5 flex flex-col p-7"
              style={{
                background: "#FFFFFF",
                border: "2px solid rgba(11,26,51,0.06)",
                borderRadius: 24,
                boxShadow: "0px 4px 0px rgba(11,26,51,0.06), 0px 12px 28px rgba(11,26,51,0.06)",
              }}
            >
              <span style={{ ...FREDOKA, color: "#0065F8", fontSize: 11, fontWeight: 800, letterSpacing: "0.18em", textTransform: "uppercase" }}>
                Choose Plan
              </span>
              <h3 style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 22, textTransform: "uppercase", letterSpacing: "-0.01em", margin: "4px 0 18px" }}>
                Pick a length
              </h3>

              <div className="flex flex-col gap-3 flex-1">
                {GIFT_PLANS.map((plan) => {
                  const isSelected = selectedPlanId === plan.id;
                  return (
                    <label key={plan.id} className="relative block cursor-pointer transition-all active:scale-[0.99]">
                      <input
                        type="radio"
                        name="plan"
                        value={plan.id}
                        checked={isSelected}
                        onChange={() => setSelectedPlanId(plan.id)}
                        className="peer sr-only"
                      />
                      <div
                        className="relative overflow-hidden"
                        style={{
                          padding: "16px 18px",
                          background: isSelected ? "rgba(0,101,248,0.06)" : "#FFFFFF",
                          border: isSelected ? "2px solid #0065F8" : "2px solid rgba(11,26,51,0.08)",
                          borderRadius: 16,
                          boxShadow: isSelected
                            ? "0px 3px 0px #073B8E, 0px 10px 22px rgba(0,101,248,0.18)"
                            : "0px 2px 0px rgba(11,26,51,0.05)",
                        }}
                      >
                        {plan.popular && (
                          <div
                            style={{
                              position: "absolute",
                              top: 0,
                              right: 0,
                              ...FREDOKA,
                              background: "#0065F8",
                              color: "#FFFFFF",
                              fontWeight: 800,
                              fontSize: 9,
                              letterSpacing: "0.16em",
                              textTransform: "uppercase",
                              padding: "4px 10px",
                              borderBottomLeftRadius: 12,
                              boxShadow: "0px 2px 0px #073B8E",
                            }}
                          >
                            Most Popular
                          </div>
                        )}
                        <div className="flex justify-between items-center gap-3">
                          <div>
                            <div style={{ ...FREDOKA, color: "#0B1A33", fontWeight: 700, fontSize: 16, lineHeight: 1.2 }}>{plan.label}</div>
                            <div style={{ ...FREDOKA, color: "#5A6B85", fontSize: 12, marginTop: 2 }}>{plan.desc}</div>
                          </div>
                          <div className="text-right">
                            <div style={{ ...FREDOKA, color: "#0065F8", fontWeight: 800, fontSize: 22, lineHeight: 1 }}>{plan.price}</div>
                            {plan.oldPrice && (
                              <div style={{ ...FREDOKA, color: "#94A3B8", fontWeight: 700, fontSize: 10, textDecoration: "line-through", marginTop: 2 }}>{plan.oldPrice}</div>
                            )}
                          </div>
                        </div>
                      </div>
                    </label>
                  );
                })}
              </div>

              <div className="mt-5 pt-4" style={{ borderTop: "2px solid rgba(11,26,51,0.06)" }}>
                <button
                  type="submit"
                  disabled={isContinueDisabled}
                  className="w-full inline-flex items-center justify-center gap-2 transition active:scale-[0.97] disabled:opacity-50 disabled:cursor-not-allowed"
                  style={{
                    ...FREDOKA,
                    background: "#0065F8",
                    color: "#FFFFFF",
                    fontWeight: 700,
                    fontSize: 15,
                    border: "none",
                    borderRadius: 999,
                    padding: "14px 22px",
                    boxShadow: "0px 4px 0px #073B8E, 0px 12px 24px rgba(0,101,248,0.22)",
                    cursor: "pointer",
                  }}
                >
                  {isSubmitting ? (
                    <>
                      <span style={{ width: 14, height: 14, borderRadius: "50%", border: "2px solid rgba(255,255,255,0.30)", borderTopColor: "#FFFFFF", animation: "gp-spin 1s linear infinite" }} />
                      Redirecting…
                    </>
                  ) : (
                    <>
                      Continue to Payment
                      <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2.6" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" d="M9 5l7 7-7 7" />
                      </svg>
                    </>
                  )}
                </button>
                <p className="text-center mt-3 inline-flex items-center justify-center gap-1.5 w-full" style={{ ...FREDOKA, color: "#94A3B8", fontSize: 11, fontWeight: 600, letterSpacing: "0.12em", textTransform: "uppercase" }}>
                  <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2.2" viewBox="0 0 24 24">
                    <rect x="3" y="11" width="18" height="11" rx="2" />
                    <path d="M7 11V7a5 5 0 0110 0v4" />
                  </svg>
                  Secure checkout · no auto-renewal
                </p>
              </div>
            </section>
          </div>
        </form>
      </div>

      <style>{`@keyframes gp-spin { to { transform: rotate(360deg); } }`}</style>
    </div>
  );
}
